import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Routes } from '@angular/router';
import { MatTabsModule } from '@angular/material/tabs';
import { OneSlopeComponent } from './steps/measurements/one-slope/one-slope.component';
import { SharedModule } from '../common/shared/shared.module';
import { MatStepperModule } from '@angular/material/stepper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { LocalizationModule } from '../localization/localization.module';
import { ModelsComponent } from './steps/models/models.component';
import { AccessoriesComponent } from './steps/accessories/accessories.component';
import { CalculationComponent } from './calculation/calculation.component';
import { FourSlopesComponent } from './steps/measurements/four-slopes/four-slopes.component';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { LoadCalculationComponent } from './load-calculation/load-calculation.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatCardModule } from '@angular/material/card';
import { MatRadioModule } from '@angular/material/radio';
import { MeasurementsComponent } from './steps/measurements/measurements.component';
import { ComponentsModule } from '../components/components.module';
import { NgxGoogleAnalyticsModule } from 'ngx-google-analytics';
import { MatDialogModule } from '@angular/material/dialog';
import { CustomComponent } from './steps/measurements/custom/custom.component';
import { RainSystemComponent } from './steps/measurements/rain-system/rain-system.component';
import { AnnexModalComponent } from './steps/measurements/annex-modal/annex-modal.component';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { CustomShapeComponent } from './steps/measurements/custom-shape/custom-shape.component';
import { CalculationTypeRoutes } from './calculation.constants';
import { ModifyPointDialogComponent } from './steps/measurements/custom-shape/modify-point-dialog/modify-point-dialog.component';
import { DrawShapeService } from './steps/measurements/custom-shape/draw-shape.service';
import { ModifyTileDialogComponent } from './steps/measurements/custom-shape/modify-tile-dialog/modify-tile-dialog.component';
import { SaveShapeDialogComponent } from './steps/measurements/custom-shape/save-shape-dialog/save-shape-dialog.component';
import { MatMenuModule } from '@angular/material/menu';
import { AddPointDialogComponent } from './steps/measurements/custom-shape/add-point-dialog/add-point-dialog.component';
import { SketchesComponent } from './steps/sketches/sketches.component';
import { StepNavigationComponent } from './steps/step-navigation/step-navigation.component';
import { LoadCalculationService } from './load-calculation/load-calculation.service';
import { AddAccessoryDialogComponent } from './steps/measurements/custom-shape/add-accessory-dialog/add-accessory-dialog.component';
import { FullscreenOverlayContainer, OverlayContainer } from '@angular/cdk/overlay';
import { CalculationTypesComponent } from '../common/shared/calculation-types/calculation-types.component';
import { AuthGuard } from '../auth/auth.guard';
import { FenceSlatComponent } from './steps/measurements/fence-slat/fence-slat.component';

const routes: Routes = [
  {
    path: '',
    component: CalculationTypesComponent,
    data: {
      title: 'Calculatoare acoperiș',
      breadcrumb: 'Calculatoare',
      metaDescription:
        'Află prețul prin calculatoarele noastre rapid și precis. Introduceți dimensiunile și selectați materialele pentru o estimare personalizată.',
    },
  },
  {
    path: `${CalculationTypeRoutes.FENCE_SLAT}/:step`,
    component: CalculationComponent,
    data: {
      roofType: CalculationTypeRoutes.FENCE_SLAT,
      title: 'Calculator sipca gard',
      subtitle: 'Calculator gard de tip sipca - Completeaza datele',
      h3: 'Calculator gard de tip sipca - Obtine pretul imediat',
      breadcrumb: 'Sipca gard',
      metaDescription:
        'Calculator gard de tip sipca - Află prețul prin calculatorul nostru rapid și precis. Introduceți dimensiunile și selectați materialele pentru o estimare personalizată.',
    },
  },
  {
    path: `${CalculationTypeRoutes.ONE_SLOPE}/:step`,
    component: CalculationComponent,
    data: {
      roofType: CalculationTypeRoutes.ONE_SLOPE,
      title: 'Calculator acoperiș într-o apă',
      subtitle: 'Calculator tabla acoperis intr-o apa - Completeaza datele',
      h3: 'Calculator acoperis intr-o apa - Obtine pretul imediat',
      breadcrumb: 'O apă',
      metaDescription:
        'Calculator acoperiș într-o apă - Află prețul prin calculatorul nostru rapid și precis. Introduceți dimensiunile și selectați materialele pentru o estimare personalizată.',
    },
  },
  {
    path: `${CalculationTypeRoutes.TWO_SLOPES}/:step`,
    component: CalculationComponent,
    data: {
      roofType: CalculationTypeRoutes.TWO_SLOPES,
      title: 'Calculator acoperiș în 2 ape',
      subtitle: 'Calculator tabla acoperis 2 ape - Completeaza datele',
      h3: 'Calculator acoperis in 2 ape - Obtine pretul imediat',
      breadcrumb: 'Două ape',
      metaDescription:
        'Calculator acoperiș în 2 ape - Află prețul prin calculatorul nostru rapid și precis. Introduceți dimensiunile și selectați materialele pentru o estimare personalizată.',
    },
  },
  {
    path: `${CalculationTypeRoutes.FOUR_SLOPES}/:step`,
    component: CalculationComponent,
    data: {
      roofType: CalculationTypeRoutes.FOUR_SLOPES,
      title: 'Calculator acoperiș în 4 ape',
      subtitle: 'Calculator tabla acoperis 4 ape - Completeaza datele',
      h3: 'Calculator acoperis in 4 ape - Obtine pretul imediat',
      breadcrumb: 'Patru ape',
      metaDescription:
        'Calculator acoperiș în 4 ape - Află prețul prin calculatorul nostru rapid și precis. Introduceți dimensiunile și selectați materialele pentru o estimare personalizată.',
    },
  },
  {
    path: `${CalculationTypeRoutes.MANUAL}/:step`,
    component: CalculationComponent,
    data: {
      roofType: CalculationTypeRoutes.MANUAL,
      title: 'Calculator acoperiș manual',
      subtitle: 'Calculator tabla acoperis manual - Completeaza datele',
      h3: 'Calculator acoperis manual - Obtine pretul imediat',
      breadcrumb: 'Calculator acoperiș manual',
      metaDescription:
        'Calculator acoperiș manual - Află prețul prin calculatorul nostru rapid și precis. Introduceți dimensiunile și selectați materialele pentru o estimare personalizată.',
    },
  },
  {
    path: `${CalculationTypeRoutes.SHAPES}/:step`,
    component: CalculationComponent,
    canActivate: [AuthGuard],
    data: {
      roofType: CalculationTypeRoutes.SHAPES,
      title: 'Calculator acoperiș cu forme geometrice',
      subtitle: 'Calculator tabla forme geometrice - Completeaza datele',
      h3: 'Calculator acoperis forme geometrice - Obtine pretul imediat',
      breadcrumb: 'Calculator acoperiș cu forme geometrice',
      metaDescription:
        'Calculator acoperiș cu forme geometrice - Află prețul prin calculatorul nostru rapid și precis. Introduceți dimensiunile și selectați materialele pentru o estimare personalizată.',
      accessDeniedMessage:
        'Aceasta metoda de calcul este rezervata pentru colaboratori Expert Tabla. Daca doresti acces te rog sa ne contactezi.',
    },
  },
  {
    path: ':calculationId',
    component: LoadCalculationComponent,
  },
  {
    path: '**',
    redirectTo: '/404',
  },
];

@NgModule({
  providers: [
    DrawShapeService,
    LoadCalculationService,
    { provide: OverlayContainer, useClass: FullscreenOverlayContainer },
  ],
  declarations: [
    AnnexModalComponent,
    CalculationComponent,
    ModelsComponent,
    AccessoriesComponent,
    OneSlopeComponent,
    FourSlopesComponent,
    LoadCalculationComponent,
    MeasurementsComponent,
    CustomComponent,
    RainSystemComponent,
    CustomShapeComponent,
    ModifyPointDialogComponent,
    ModifyTileDialogComponent,
    SaveShapeDialogComponent,
    AddPointDialogComponent,
    SketchesComponent,
    StepNavigationComponent,
    AddAccessoryDialogComponent,
    FenceSlatComponent,
  ],
  imports: [
    RouterModule.forChild(routes),
    CommonModule,
    LocalizationModule,
    MatTabsModule,
    MatSnackBarModule,
    SharedModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatTooltipModule,
    FormsModule,
    MatAutocompleteModule,
    MatCardModule,
    MatRadioModule,
    MatButtonToggleModule,
    ComponentsModule,
    NgxGoogleAnalyticsModule,
    MatDialogModule,
    MatMenuModule,
  ],
  exports: [AccessoriesComponent, StepNavigationComponent],
})
export class CalculationModule {}
